"use client"

import { useState, useEffect, useCallback } from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import { Pagination, Navigation, Keyboard } from "swiper/modules"
import Image from "next/image"
import { X, ChevronLeft, ChevronRight, Share2, Maximize2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/core/lib/utils"
import type { Swiper as SwiperType } from "swiper"

import "swiper/css"
import "swiper/css/pagination"
import { useDictionary } from "@/core/hooks/use-dictionary"

type ImageType = {
  url: string
  alt?: string
  description?: string
}

type ProductGalleryProps = {
  images: ImageType[]
  product: {
    title?: string
    discount_percent: number
    discount_title: string
    thumbnail?: ImageType
  }
  settings?: { discount_display_active?: string | null }
}

export default function ProductGallery({ images, product, settings }: ProductGalleryProps) {
  // وقتی این تنظیم غیرفعاله، برچسب تخفیف رو گالری تصویر هم نشون داده نمی‌شه
  const showDiscount = settings?.discount_display_active !== 'deactive'
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null)

  const { dictionary } = useDictionary();

  const displayImages: ImageType[] = product?.thumbnail?.url
    ? [
      {
        url: product.thumbnail.url,
        alt: product.title || "Product main image",
      },
      ...images,
    ]
    : images

  // Check mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Lock body scroll when modal open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [isModalOpen])

  const openModal = (index: number) => {
    setCurrentIndex(index)
    setIsModalOpen(true)
  }

  const closeModal = () => setIsModalOpen(false)

  const goToPrevious = useCallback(() => {
    swiperInstance?.slidePrev()
  }, [swiperInstance])

  const goToNext = useCallback(() => {
    swiperInstance?.slideNext()
  }, [swiperInstance])

  // Keyboard navigation
  useEffect(() => {
    if (!isModalOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal()
      if (e.key === "ArrowLeft") goToPrevious()
      if (e.key === "ArrowRight") goToNext()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isModalOpen, goToPrevious, goToNext])

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.title || "Product",
          text: "Check out this product",
          url: window.location.href,
        })
      } catch (err) {
        console.log("Error sharing:", err)
      }
    }
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  if (displayImages.length === 0) return null

  return (
    <div className="w-full">
      {/* MOBILE: Swiper Horizontal */}
      {isMobile ? (
        <div className="relative mb-4">
          <Swiper
            modules={[Pagination]}
            spaceBetween={0}
            slidesPerView={1}
            pagination={{
              clickable: true,
              bulletActiveClass: "opacity-100 scale-125",
              bulletClass: "swiper-pagination-bullet-custom"
            }}
            speed={700}
            effect="fade"
            fadeEffect={{
              crossFade: true
            }}
            onSwiper={setSwiperInstance}
            onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex)}
            className="w-full aspect-square"
          >
            {displayImages.map((img, index) => (
              <SwiperSlide key={index} className="relative w-full h-full cursor-pointer" onClick={() => openModal(index)}>
                <Image
                  src={img.url || "/placeholder.svg"}
                  alt={img.alt || product.title || "Product image"}
                  fill
                  className="object-cover"
                  sizes="100vw"
                  priority={index === 0}
                />
                {index === 0 && product.discount_percent > 0 && showDiscount && <Badge variant="destructive" className="absolute top-2 right-2 ltr:left-2 text-xs font-semibold rounded-md px-2.5 py-1 shadow-sm">
                  {product.discount_percent}% {dictionary.ui?.cart.discount ?? 'تخفیف'}
                </Badge>
                }
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Minimal Dot Indicator */}
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-4">
            {displayImages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => swiperInstance?.slideTo(idx)}
                className={cn(
                  "rounded-full transition-all duration-300",
                  idx === currentIndex
                    ? "bg-primary-0 size-1.5 ring-2 ring-primary-900"
                    : "bg-primary-900 size-1 hover:bg-foreground/50"
                )}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      ) : (
        /* DESKTOP: Two Column Grid */
        <div className={cn("grid", displayImages.length === 1 ? "grid-cols-1" : "grid-cols-2")}>
          {displayImages.map((img, index) => (
            <div
              key={index}
              className="relative cursor-pointer"
              onClick={() => openModal(index)}
            >
              <Image
                src={img.url || "/placeholder.svg"}
                alt={img.alt || product.title || "Product image"}
                width={600}
                height={800}
                className="w-full h-auto p-0.5"
                sizes="50vw"
                priority={index < 2}
                style={{
                  borderImageSlice: '4 4 4 4',
                  borderImageWidth: '4px 4px 4px 4px',
                  borderImageOutset: '.5px .5px .5px .5px',
                  borderImageRepeat: 'round round',
                  borderImageSource: 'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABUAAAAVCAYAAACpF6WWAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAB3SURBVHgB7dVBCoAgFEXR911JjcRVVSurXamj2on5wiCkWd9RnpkiF0FRCSEkZCKyWWsXPHjvhzy/o5JSGp1zR7V2y2una3BHtbBnuEMo0u51Ba8NFLFn3u7hF+wZNPD3KB8HKNLudUWMcYUi9nj6MxSxJy3+qBOdU0PNJhRu4gAAAABJRU5ErkJggg==)',
                  borderStyle: 'solid',
                  imageRendering: 'pixelated'
                }}
              />

              {index === 0 && product.discount_percent > 0 && showDiscount && <Badge variant="destructive" className="absolute top-2 right-2 ltr:left-2 text-sm font-semibold rounded-md px-2.5 py-1 shadow-sm">
                {product.discount_percent}% {dictionary.ui?.cart.discount ?? 'تخفیف'}
              </Badge>}
            </div>
          ))}
        </div>
      )}

      {/* Lightbox Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-background animate-in fade-in duration-200">
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between p-4">
            <span className="text-foreground font-medium">
              {currentIndex + 1} / {displayImages.length}
            </span>

            <div className="flex items-center gap-1">
              <Button
                size="icon"
                variant="ghost"
                onClick={toggleFullscreen}
                className="h-10 w-10"
              >
                <Maximize2 className="w-5 h-5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={handleShare}
                className="h-10 w-10"
              >
                <Share2 className="w-5 h-5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={closeModal}
                className="h-10 w-10"
              >
                <X className="w-6 h-6" />
              </Button>
            </div>
          </div>

          {/* Modal Swiper */}
          <div className="absolute inset-0 flex items-center justify-center p-4 pt-16 pb-20">
            <Swiper
              modules={[Navigation, Keyboard]}
              initialSlide={currentIndex}
              slidesPerView={1}
              loop={displayImages.length > 1}
              keyboard={{ enabled: true }}
              navigation={{
                prevEl: ".modal-prev",
                nextEl: ".modal-next",
              }}
              onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex)}
              className="w-full h-full"
            >
              {displayImages.map((img, i) => (
                <SwiperSlide key={i} className="!flex items-center justify-center">
                  <Image
                    src={img.url || "/placeholder.svg"}
                    alt={img.alt || "Product image"}
                    width={1920}
                    height={1080}
                    className="max-w-full max-h-full w-auto h-auto object-contain"
                    priority={i === currentIndex}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Navigation Buttons */}
          {displayImages.length > 1 && (
            <>
              <Button
                size="icon"
                variant="secondary"
                className="modal-prev absolute left-2 top-1/2 -translate-y-1/2 z-50 h-10 w-10 rounded-none"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button
                size="icon"
                variant="secondary"
                className="modal-next absolute right-2 top-1/2 -translate-y-1/2 z-50 h-10 w-10 rounded-none"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </>
          )}

          {/* Thumbnail Strip */}
          <div className="absolute bottom-0 left-0 right-0 z-50 p-4 border-t">
            <div className="flex gap-2 justify-center overflow-x-auto">
              {displayImages.map((img, index) => (
                <button
                  key={index}
                  onClick={() => {
                    const modalSwiper = document.querySelector(".swiper") as any
                    modalSwiper?.swiper?.slideTo(index)
                  }}
                  className={cn(
                    "relative shrink-0 w-14 h-14 overflow-hidden transition-opacity",
                    currentIndex === index
                      ? "opacity-100"
                      : "opacity-40 hover:opacity-70"
                  )}
                >
                  <Image
                    src={img.url || "/placeholder.svg"}
                    alt={`Thumbnail ${index + 1}`}
                    fill
                    className="object-cover"
                    sizes="56px"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}